import { NavigationSettings } from '@/types/NavigationSettings'
import { OffsetMap } from '@/types/offsetMap'

/**
 * Helper function to filter candidates based on navigationSettings
 * Applies connectivity, 3D distance, and angle filters
 * Returns array of valid frame indices that match all active filters
 */
export function getFilteredCandidates(
  allFrameIndices: number[],
  navigationSettings: NavigationSettings | null,
  offsetMap: OffsetMap | null,
  connectivityGraph: Map<number, Set<number>> | null,
  currentFrameIndex: number,
  reconstructionMethod?: 'colmap' | 'phase_correlation'
): number[] {
  if (!navigationSettings) {
    // If no settings, return all frames
    return allFrameIndices
  }

  let validIndices = allFrameIndices

  // Filter 1: Connectivity - only show frames connected to current frame
  if (navigationSettings.useConnectivityFiltering && connectivityGraph) {
    const connected = connectivityGraph.get(currentFrameIndex) || new Set()
    validIndices = [
      currentFrameIndex,
      ...Array.from(connected).filter(idx => allFrameIndices.includes(idx))
    ]
  }

  // Filter 2: 3D Distance - only show frames within max 3D distance
  if (navigationSettings.use3DDistanceFilter && offsetMap) {
    validIndices = validIndices.filter(idx => {
      // Current frame always passes (no distance check for self)
      if (idx === currentFrameIndex) {
        return true
      }
      const frameData = offsetMap.all_pairs[`${currentFrameIndex}_${idx}`] || 
                       offsetMap.all_pairs[`${idx}_${currentFrameIndex}`]
      const distance3d = frameData?.distance_3d ?? Infinity
      return distance3d <= navigationSettings.max3DDistance
    })
  }

  // Filter 3: Delta Angle - only show frames with similar camera angle
  if (navigationSettings.useDeltaAngleFilter && offsetMap) {
    validIndices = validIndices.filter(idx => {
      // Current frame always passes (no angle check for self)
      if (idx === currentFrameIndex) {
        return true
      }
      const frameData = offsetMap.all_pairs[`${currentFrameIndex}_${idx}`] || 
                       offsetMap.all_pairs[`${idx}_${currentFrameIndex}`]
      const angleDiff = frameData?.angle_diff ?? Infinity
      return angleDiff <= navigationSettings.maxDeltaAngle
    })
  }

  // Filter 3.5: Universal Window-Based Filtering (ALWAYS APPLIED)
  // - For phase correlation: use sequentialWindow (±5 frames) for strict frame-by-frame navigation
  // - For COLMAP: use contextualWindow (±15 frames) for connectivity-aware navigation
  if (reconstructionMethod === 'phase_correlation' && navigationSettings.sequentialWindow !== null) {
    // Phase correlation: strict sequential window
    const maxDistance = navigationSettings.sequentialWindow ?? 5
    validIndices = validIndices.filter(idx => {
      const frameDiff = Math.abs(idx - currentFrameIndex)
      return frameDiff <= maxDistance
    })
  } else if (navigationSettings.contextualWindow !== null) {
    // COLMAP or default: contextual window (hard limit)
    const maxDistance = navigationSettings.contextualWindow ?? 15
    validIndices = validIndices.filter(idx => {
      const frameDiff = Math.abs(idx - currentFrameIndex)
      return frameDiff <= maxDistance
    })
  }

  // Filter 4: Max Candidates Limit - respect the maximum candidate count
  if (navigationSettings.maxCandidates !== null && validIndices.length > navigationSettings.maxCandidates) {
    const current = currentFrameIndex
    const isCurrent = validIndices.includes(current)
    
    let toKeep = validIndices.filter(i => i !== current)
    
    // Sort by 3D distance if available
    if (offsetMap) {
      toKeep.sort((a, b) => {
        const dataA = offsetMap.all_pairs[`${currentFrameIndex}_${a}`] || 
                      offsetMap.all_pairs[`${a}_${currentFrameIndex}`]
        const dataB = offsetMap.all_pairs[`${currentFrameIndex}_${b}`] || 
                      offsetMap.all_pairs[`${b}_${currentFrameIndex}`]
        const distA = dataA?.distance_3d ?? Infinity
        const distB = dataB?.distance_3d ?? Infinity
        return distA - distB
      })
    }
    
    toKeep = toKeep.slice(0, navigationSettings.maxCandidates - (isCurrent ? 1 : 0))
    
    if (isCurrent) {
      validIndices = [current, ...toKeep]
    } else {
      validIndices = toKeep
    }
  }

  return validIndices
}

/**
 * Get pairs dictionary from offsetMap
 * Supports both old format (sequential) and new format (all_pairs)
 */
export function getOffsetMapPairs(offsetMapData: any): Record<string, any> {
  if (!offsetMapData) return {}
  return offsetMapData.all_pairs || offsetMapData.sequential || {}
}
